/**
 * Pure H OS · Sprint 2 · Notification Center page.
 * Composes Card, SectionHeader, SeverityBadge, TabBar, ListRow.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AtSign, Sparkles, AlertOctagon, ClipboardCheck, Bell, Check, Inbox } from "lucide-react";
import { usePlatform } from "../context/PlatformContext";
import { Card, SectionHeader, SeverityBadge, EmptyState } from "../components/lib";
import { TabBar, ListRow, Kbd } from "../components/lib.overlay";
import { NotificationKind } from "../mocks/platformData";

const KIND_META: Record<NotificationKind, { icon: any; tint: string; fg: string; label: string }> = {
  mention:  { icon: AtSign,        tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)", label: "Mention"  },
  ai:       { icon: Sparkles,      tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)",            label: "AI"       },
  priority: { icon: AlertOctagon,  tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",        label: "Priority" },
  approval: { icon: ClipboardCheck,tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)",       label: "Approval" },
  system:   { icon: Bell,          tint: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)",     label: "System"   },
};

type Tab = "unread" | "all" | "priority" | "mentions" | "ai" | "read";

export default function Notifications() {
  const { notifications, unreadCount, markRead, markAllRead } = usePlatform();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("unread");

  const counts = useMemo(() => ({
    unread:   notifications.filter((n) => !n.read).length,
    all:      notifications.length,
    priority: notifications.filter((n) => n.kind === "priority").length,
    mentions: notifications.filter((n) => n.kind === "mention").length,
    ai:       notifications.filter((n) => n.kind === "ai").length,
    read:     notifications.filter((n) => n.read).length,
  }), [notifications]);

  const filtered = useMemo(() => {
    switch (tab) {
      case "unread":   return notifications.filter((n) => !n.read);
      case "read":     return notifications.filter((n) => n.read);
      case "priority": return notifications.filter((n) => n.kind === "priority");
      case "mentions": return notifications.filter((n) => n.kind === "mention");
      case "ai":       return notifications.filter((n) => n.kind === "ai");
      default:         return notifications;
    }
  }, [tab, notifications]);

  return (
    <div data-testid="pureh-notifications">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Inbox</div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Notification Center</h1>
        <div className="mt-1 text-sm text-slate-500">
          {unreadCount === 0
            ? "You're all caught up."
            : `${unreadCount} unread across mentions, priorities, and AI signals.`}
        </div>
      </div>

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <TabBar<Tab>
          testId="notif-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "unread",   label: "Unread",    count: counts.unread   },
            { key: "priority", label: "Priority",  count: counts.priority },
            { key: "mentions", label: "Mentions",  count: counts.mentions },
            { key: "ai",       label: "AI",        count: counts.ai       },
            { key: "all",      label: "All",       count: counts.all      },
            { key: "read",     label: "Read",      count: counts.read     },
          ]}
        />
        <button
          data-testid="notif-mark-all"
          onClick={markAllRead}
          className="inline-flex items-center gap-1.5 text-xs font-medium"
          style={{ color: "var(--ph-brand-primary)" }}
        >
          <Check size={13} /> Mark all read
        </button>
      </div>

      <Card padded={false} testId="notif-list-card">
        <div className="p-5 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
          <SectionHeader
            eyebrow="Signals"
            title={
              tab === "unread"   ? "What needs your attention" :
              tab === "priority" ? "Priority queue" :
              tab === "mentions" ? "You were mentioned" :
              tab === "ai"       ? "AI notifications" :
              tab === "read"     ? "Read" : "Everything"
            }
            action={<Inbox size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
        </div>

        <div className="p-2" data-testid="notif-list">
          {filtered.length === 0 ? (
            <EmptyState title="Nothing here" hint="This inbox is clear." />
          ) : filtered.map((n) => {
            const meta = KIND_META[n.kind];
            const Icon = meta.icon;
            return (
              <ListRow
                key={n.id}
                testId={`notif-item-${n.id}`}
                onClick={() => {
                  markRead(n.id);
                  if (n.href) navigate(n.href);
                }}
                icon={
                  <span
                    className="inline-flex items-center justify-center"
                    style={{
                      width: 28, height: 28,
                      borderRadius: "999px",
                      background: meta.tint, color: meta.fg,
                    }}
                  >
                    <Icon size={14} />
                  </span>
                }
                label={
                  <span className="inline-flex items-center gap-2">
                    {!n.read && (
                      <span
                        style={{
                          width: 6, height: 6, borderRadius: "999px",
                          background: "var(--ph-brand-primary)",
                          display: "inline-block",
                        }}
                      />
                    )}
                    <span className="truncate">{n.title}</span>
                  </span>
                }
                sublabel={
                  <>
                    <span style={{ color: "var(--ph-ink-default)" }}>{n.actor}</span>
                    <span> · {n.timestamp} · {meta.label}</span>
                    {n.body && <><br /><span>{n.body}</span></>}
                  </>
                }
                trailing={n.severity ? <SeverityBadge severity={n.severity} /> : undefined}
              />
            );
          })}
        </div>
      </Card>

      <div className="mt-4 flex items-center justify-end text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
        <span className="inline-flex items-center gap-1.5">
          Shortcut: <Kbd>N</Kbd> toggles the drawer · <Kbd>M</Kbd> marks all read
        </span>
      </div>
    </div>
  );
}
